import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { User01Icon, Settings01Icon } from '@untitledui-icons/react/line'
import { cn } from '@/lib/utils'
import type { Message, PartnerReview } from '@/types/conversation'
import { ToolCallDisplay } from './ToolCallDisplay'
import {
  ReviewBanner,
  ReviewFlagButton,
  ReviewForm,
  type TranscriptReviewProps,
} from './TranscriptReview'

interface ConversationTranscriptProps {
  messages?: Message[] | string
  className?: string
  /** Assistant configuration (for checking screenshot support) */
  assistantConfig?: unknown
  /** Date when the conversation took place (for screenshot URL generation) */
  conversationDate?: string
  /** Per-turn review controls; only provided by the conversation drawer for admins */
  review?: TranscriptReviewProps
}

interface DisplayMessage {
  message: Message
  /** Index in the raw messages array, used to address a turn in reviews */
  rawIndex: number
}

// Simple AI avatar component
function AIAvatar() {
  return (
    <div className="bg-brand-500 flex h-8 w-8 shrink-0 items-center justify-center rounded-full">
      <span className="text-xs font-bold text-white">AI</span>
    </div>
  )
}

function UserAvatar() {
  return (
    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-blue-300 bg-blue-50 dark:border-blue-700 dark:bg-blue-900">
      <User01Icon className="h-4 w-4 text-blue-500" />
    </div>
  )
}

export function SystemAvatar({ label, className }: { label?: string; className?: string }) {
  return (
    <div
      role={label ? 'img' : undefined}
      aria-label={label}
      title={label}
      className={cn(
        'flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900',
        className
      )}
    >
      <Settings01Icon className="h-4 w-4 text-amber-600 dark:text-amber-400" />
    </div>
  )
}

export function ConversationTranscript({
  messages,
  className,
  assistantConfig,
  conversationDate,
  review,
}: ConversationTranscriptProps) {
  const { t } = useTranslation()
  const [openReviewIndex, setOpenReviewIndex] = useState<number | null>(null)

  const parsedMessages = useMemo(() => {
    if (!messages) return []
    if (Array.isArray(messages)) return messages

    try {
      return JSON.parse(messages)
    } catch {
      return []
    }
  }, [messages])

  // Filter and process messages, keeping the raw array index for review addressing
  const displayMessages = useMemo<DisplayMessage[]>(() => {
    if (!parsedMessages.length) return []

    // Skip first message (usually system prompt) and filter empties
    return parsedMessages
      .map((message: Message, rawIndex: number) => ({ message, rawIndex }))
      .slice(1)
      .filter(
        ({ message }: DisplayMessage) =>
          message &&
          ((message.content !== '' && message.content !== "''" && message.content !== '""') ||
            message.tool_calls)
      )
  }, [parsedMessages])

  const reviewByIndex = useMemo(() => {
    const map = new Map<number, PartnerReview>()
    review?.reviews.forEach((entry) => map.set(entry.message_index, entry))
    return map
  }, [review?.reviews])

  // Helper to truncate after "The call has ended"
  const truncateAfterEnd = (content: string | null) => {
    if (!content) return content
    const baseText = 'The call has ended'
    const endIndex = content.indexOf(baseText)
    if (endIndex !== -1) {
      return content.substring(0, endIndex + baseText.length).trim()
    }
    return content
  }

  if (!displayMessages.length) {
    return (
      <div className={cn('py-8 text-center text-gray-500', className)}>
        {t('resources.conversations.no_transcript_available')}
      </div>
    )
  }

  return (
    <div className={cn('flex flex-col gap-4', className)}>
      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
        {t('resources.conversations.tabs.transcript')}
      </h3>

      {review && <ReviewBanner reviews={review.reviews} />}

      <div className="space-y-4">
        {displayMessages.map(({ message, rawIndex }) => {
          // Skip tool responses as they'll be shown with their tool calls
          if (message.role === 'tool' && message.tool_call_id) {
            return null
          }

          const isUser = message.role === 'user'
          const isSystem = message.role === 'system'
          const isAssistant = message.role === 'assistant'
          const hasToolCalls = message.tool_calls && message.tool_calls.length > 0

          const existingReview = review ? reviewByIndex.get(rawIndex) : undefined
          const isReviewable = Boolean(
            review && isAssistant && (review.reviewableIndices.includes(rawIndex) || existingReview)
          )
          const isFlagged = existingReview?.flagged ?? false

          return (
            <div
              key={`message-${rawIndex}`}
              className={cn('flex gap-3', isUser ? 'flex-row' : 'flex-row-reverse')}
            >
              {/* Avatar */}
              {isSystem ? (
                <SystemAvatar />
              ) : isAssistant || hasToolCalls ? (
                <AIAvatar />
              ) : (
                <UserAvatar />
              )}

              {/* Message bubble with optional review form below it */}
              <div
                className={cn(
                  'flex max-w-[80%] min-w-0 flex-col gap-2',
                  isUser ? 'items-start' : 'items-end'
                )}
              >
                <div
                  className={cn(
                    'rounded-lg p-3',
                    isUser
                      ? 'bg-gray-100 dark:bg-gray-800'
                      : hasToolCalls
                        ? 'bg-brand-100 dark:bg-brand-900'
                        : 'bg-brand-500 text-white',
                    isFlagged && 'ring-2 ring-red-500'
                  )}
                >
                  {message.content && (
                    <p
                      className={cn(
                        'text-sm wrap-anywhere',
                        isUser
                          ? 'text-gray-900 dark:text-gray-100'
                          : hasToolCalls
                            ? 'text-gray-900 dark:text-gray-100'
                            : 'text-white'
                      )}
                    >
                      {truncateAfterEnd(message.content)}
                    </p>
                  )}

                  {/* Tool calls */}
                  {hasToolCalls &&
                    message.tool_calls!.map((toolCall) => {
                      const toolResponse = displayMessages.find(
                        (entry) =>
                          entry.message.role === 'tool' &&
                          entry.message.tool_call_id === toolCall.id
                      )?.message
                      return (
                        <ToolCallDisplay
                          key={toolCall.id}
                          toolCall={toolCall}
                          response={toolResponse?.content}
                          assistantConfig={assistantConfig}
                          conversationDate={conversationDate}
                        />
                      )
                    })}
                </div>

                {isFlagged && openReviewIndex !== rawIndex && existingReview?.comment && (
                  <p className="text-xs text-red-600 dark:text-red-400">{existingReview.comment}</p>
                )}

                {review && isReviewable && openReviewIndex === rawIndex && (
                  <ReviewForm
                    existingReview={existingReview}
                    onCancel={() => setOpenReviewIndex(null)}
                    onSubmit={async (flagged, comment) => {
                      await review.onSubmit({
                        messageIndex: rawIndex,
                        flagged,
                        comment,
                      })
                      setOpenReviewIndex(null)
                    }}
                  />
                )}
              </div>

              {/* Review flag trigger */}
              {isReviewable && (
                <ReviewFlagButton
                  isFlagged={isFlagged}
                  onPress={() => setOpenReviewIndex(openReviewIndex === rawIndex ? null : rawIndex)}
                  className="self-center"
                />
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default ConversationTranscript
